import { useCallback, useRef, useState } from 'react'
import { useInterval } from 'react-use'
import { shallow } from 'zustand/shallow'
import { useAudioEngine } from '../use-audio-engine'
import { PlayerApi } from '../use-player-methods/use-player-methods'

interface UseTransitionAudios {
  applyEffectFadeOut: () => void
}

interface Props {
  playerApi: PlayerApi
  loadInstruments: () => void
}

export const useTransitionAudios = ({
  playerApi,
  loadInstruments
}: Props): UseTransitionAudios => {
  const bkpMasterVolume = useRef(1)
  const [initIntervalLoadChannels, setInitIntervalLoadChannels] =
    useState(false)

  const audioEngine = useAudioEngine((p: any) => p.engine)
  const masterVolumeVal = useAudioEngine((p) => p.state.masterVolume)
  const hasAllChannelsReadyToPlay = useAudioEngine((player) => {
    return Object.keys(player.channels).every(
      (channel) => player.channels[channel]?.ready
    )
  }, shallow)

  const applyEffectFadeOut = useCallback(() => {
    // enabled effect only for superpowered
    if (audioEngine === 'wavesjs') {
      return
    }

    let newMasterVolume = masterVolumeVal
    bkpMasterVolume.current = masterVolumeVal

    const fadeOut = setInterval(() => {
      if (newMasterVolume > 0) {
        newMasterVolume -= 0.1
        playerApi.masterVolume(newMasterVolume)
      } else {
        clearInterval(fadeOut)
        loadInstruments()
        setInitIntervalLoadChannels(true)
      }
    }, 100)
  }, [playerApi, masterVolumeVal, audioEngine, loadInstruments])

  const applyEffectFadeIn = useCallback(() => {
    let newMasterVolume = masterVolumeVal

    const fadeIn = setInterval(() => {
      if (newMasterVolume < bkpMasterVolume.current) {
        newMasterVolume += 0.1
        if (newMasterVolume > 1) {
          newMasterVolume = 1
        }
        playerApi.masterVolume(newMasterVolume)
      } else {
        clearInterval(fadeIn)
      }
    }, 100)
  }, [playerApi, masterVolumeVal])

  useInterval(
    () => {
      if (hasAllChannelsReadyToPlay) {
        setInitIntervalLoadChannels(false)
        applyEffectFadeIn()
      }
    },
    initIntervalLoadChannels ? 300 : null
  )

  return {
    applyEffectFadeOut
  }
}
